import { User } from 'fitify-types/src/types/user'
import { isDefined } from 'fitify-utils/src/properties'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import type { FunnelRoutesDefinition } from 'types/routes'
import { type DataPathState, Funnel, Question } from 'types/state'

import { getSubscriptionFlowPages } from '../../utils/funnel-pages'

type MILICAM01DataState = DataPathState<Funnel.MILICAM01>

const pages = {
  welcomeAgeBuckets: {
    path: '/milicam01/welcome-age-buckets',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    isExcludedFromProgressBarOrStepper: true,
    stateKey: 'age_bucket',
    id: 'welcome_age_buckets',
  },
  socialProofMillions: {
    path: '/milicam01/social-proof-millions',
    isValidatedStep: true,
    id: 'social_proof_millions',
  },
  experience: {
    path: '/milicam01/experience',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'experience',
    id: 'experience',
  },
  experiencePitch: {
    path: '/milicam01/experience-pitch',
    isValidatedStep: true,
    id: 'experience_pitch',
    condition: (data: MILICAM01DataState) => {
      return isDefined(data.experience)
    },
  },
  mainGoal: {
    path: '/milicam01/main-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'goal',
    id: 'main_goal',
    required: true,
  },
  goalPitch: {
    path: '/milicam01/goal-pitch',
    isValidatedStep: true,
    id: 'goal_pitch',
    condition: (data: MILICAM01DataState) => {
      return isDefined(data.goal)
    },
  },
  motivationEarly: {
    path: '/milicam01/motivation-early',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'motivation_early',
    id: 'motivation_early',
  },
  didYouKnowHormones: {
    path: '/milicam01/did-you-know-hormones',
    isValidatedStep: true,
    id: 'did_you_know_hormones',
  },
  didYouKnowDepression: {
    path: '/milicam01/did-you-know-depression',
    isValidatedStep: true,
    id: 'did_you_know_depression',
  },
  didYouKnowLongevity: {
    path: '/milicam01/did-you-know-longevity',
    isValidatedStep: true,
    id: 'did_you_know_longevity',
  },
  physicalBuild: {
    path: '/milicam01/physical-build',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'current_shape',
    id: 'physical_build',
  },
  bodyGoal: {
    path: '/milicam01/body-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'body_goal',
    id: 'body_goal',
  },
  weightChangeExp: {
    path: '/milicam01/weight-change-exp',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'weight_change_exp',
    id: 'weight_change_exp',
  },
  bodyTypePitch: {
    path: '/milicam01/body-type-pitch',
    isValidatedStep: true,
    id: 'body_type_pitch',
    condition: (data: MILICAM01DataState) => {
      return isDefined(data.weight_change_exp)
    },
  },
  bestShape: {
    path: '/milicam01/best-shape',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'latest_ideal_weight',
    id: 'best_shape',
  },
  problemAreas: {
    path: '/milicam01/problem-areas',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'problem_areas',
    id: 'problem_areas',
    required: true,
  },
  struggle: {
    path: '/milicam01/struggle',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'struggle',
    id: 'struggle',
  },
  strugglePitch: {
    path: '/milicam01/struggle-pitch',
    isValidatedStep: true,
    id: 'struggle_pitch',
    condition: (data: MILICAM01DataState) => {
      return Boolean(
        data.struggle?.length &&
          data.struggle.some((item) => item !== Question.Struggle.None)
      )
    },
  },
  howOftenExercise: {
    path: '/milicam01/how-often-exercise',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_exercise',
    id: 'how_often_exercise',
  },
  exerciseBarrier: {
    path: '/milicam01/exercise-barrier',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'exercise_barrier',
    id: 'exercise_barrier',
    condition: (data: MILICAM01DataState): boolean => {
      return (
        data.how_often_exercise === Question.HowOftenExercise.Monthly ||
        data.how_often_exercise === Question.HowOftenExercise.Never
      )
    },
  },
  workoutFrequency: {
    path: '/milicam01/workout-frequency',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_frequency',
    id: 'workout_frequency',
  },
  workoutLength: {
    path: '/milicam01/workout-length',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_length',
    id: 'workout_length',
  },
  howOftenWalks: {
    path: '/milicam01/how-often-walks',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_walks',
    id: 'how_often_walks',
  },
  fitnessLevelSquats: {
    path: '/milicam01/fitness-level-squats',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_squats',
    id: 'fitness_level_squats',
  },
  fitnessLevelPushups: {
    path: '/milicam01/fitness-level-pushups',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_push_ups',
    id: 'fitness_level_pushups',
  },
  fitnessLevelPlank: {
    path: '/milicam01/fitness-level-plank',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_plank',
    id: 'fitness_level_plank',
  },
  flexible: {
    path: '/milicam01/flexible',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'flexible',
    id: 'flexible',
  },
  workSchedule: {
    path: '/milicam01/work-schedule',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'work_schedule',
    id: 'work_schedule',
  },
  typicalDayActivity: {
    path: '/milicam01/typical-day-activity',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'typical_day_activity',
    id: 'typical_day_activity',
  },
  energyLevels: {
    path: '/milicam01/energy-levels',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'energy_levels',
    id: 'energy_levels',
  },
  energyPitch: {
    path: '/milicam01/energy-pitch',
    isValidatedStep: true,
    id: 'energy_pitch',
  },
  waterIntake: {
    path: '/milicam01/water-intake',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'water_intake',
    id: 'water_intake',
  },
  sleepDuration: {
    path: '/milicam01/sleep-duration',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'sleep_duration',
    id: 'sleep_duration',
  },
  diet: {
    path: '/milicam01/diet',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'diet',
    id: 'diet',
  },
  habits: {
    path: '/milicam01/habits',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'habits',
    id: 'habits',
  },
  cravings: {
    path: '/milicam01/cravings',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'cravings',
    id: 'cravings',
  },
  smoking: {
    path: '/milicam01/smoking',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'smoking',
    id: 'smoking',
  },
  alcohol: {
    path: '/milicam01/alcohol',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'alcohol',
    id: 'alcohol',
  },
  lifeEventsStopped: {
    path: '/milicam01/life-events-stopped',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_events_stopped',
    id: 'life_events_stopped',
  },
  height: {
    path: '/milicam01/height',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'height',
    id: 'height',
    required: true,
  },
  weight: {
    path: '/milicam01/weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'weight',
    id: 'weight',
    required: true,
  },
  goalWeight: {
    path: '/milicam01/goal-weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'goal_weight',
    id: 'goal_weight',
    required: true,
  },
  age: {
    path: '/milicam01/age',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'age',
    id: 'age',
    required: true,
  },
  wellnessProfile: {
    path: '/milicam01/wellness-profile',
    isValidatedStep: true,
    id: 'wellness_profile',
    condition: (data: MILICAM01DataState) => {
      return (
        isDefined(data.typical_day_activity) &&
        isDefined(data.current_shape) &&
        isDefined(data.how_often_exercise) &&
        isDefined(data.weight_change_exp)
      )
    },
  },
  lifeEvent: {
    path: '/milicam01/life-event',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_event',
    id: 'life_event',
  },
  lifeEventDate: {
    path: '/milicam01/life-event-date',
    isValidatedStep: true,
    stateKey: 'life_event_date',
    id: 'life_event_date',
    condition: (data: MILICAM01DataState) => {
      return (
        isDefined(data.life_event) &&
        data.life_event !== User.Profile.LifeEvent.NoEvent
      )
    },
  },
  lifeEventGraph: {
    path: '/milicam01/life-event-graph',
    isValidatedStep: true,
    id: 'life_event_graph',
    condition: (data: MILICAM01DataState) => {
      return isDefined(data.goal_weight) && isDefined(data.weight)
    },
  },
  credibilityPitch: {
    path: '/milicam01/credibility-pitch',
    isValidatedStep: true,
    id: 'credibility_pitch',
  },
  planGenerator: {
    path: '/milicam01/plan-generator',
    id: 'plan_generator',
  },
  email: {
    path: '/milicam01/email',
    id: 'email',
    stateKey: 'email',
  },
  name: {
    path: '/milicam01/name',
    isValidatedStep: true,
    stateKey: 'displayName',
    id: 'name',
  },
  namePitch: {
    path: '/milicam01/name-pitch',
    isValidatedStep: true,
    id: 'name_pitch',
  },
  discountReveal: {
    path: '/milicam01/discount-reveal',
    isValidatedStep: true,
    id: 'discount_reveal',
    condition: (_, { promoCode }) => {
      return Boolean(!promoCode || promoCode === DEFAULT_PROMO_CODE)
    },
  },
  ...getSubscriptionFlowPages(Funnel.MILICAM01),
} as const satisfies FunnelRoutesDefinition<MILICAM01DataState>['pages']

const pagesMap = Object.values(pages)

const detectLocationPagesMap = [
  pages.planGenerator,
  pages.subscription,
  pages.exclusiveOffer,
] as const satisfies FunnelRoutesDefinition<MILICAM01DataState>['detectLocationPagesMap']

const noLeaveDialogPagesMap = [
  pages.subscription,
  pages.success,
] as const satisfies FunnelRoutesDefinition<MILICAM01DataState>['noLeaveDialogPagesMap']

export const MILICAM01_ROUTES = {
  detectLocationPagesMap,
  noLeaveDialogPagesMap,
  pages,
  pagesMap,
} as const satisfies FunnelRoutesDefinition<MILICAM01DataState>
